import React, { useState, useEffect } from 'react';
import { collection, query, onSnapshot, where } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { addPayment, deletePayment, transferShare, uploadReceiptPhoto } from '../../services/kurbanService';
import { formatWhatsAppNumber } from '../../utils/phoneUtils';
import { useAuth } from '../../context/AuthContext';
import { Wallet, PlusCircle, Loader2, FileText, Calendar, Trash2, AlertCircle, RefreshCw, ChevronRight, Printer, Paperclip, ImageIcon, MessageCircle } from 'lucide-react';
import ReceiptTemplate from './ReceiptTemplate';

export default function PaymentModal({ 
  isOpen, 
  onClose, 
  liveShare, 
  allAnimals, 
  currentUserUid,
  initialTransferMode = false
}) {
  const { currentUser, isSuperAdmin, canDeleteFinance } = useAuth();
  const [payments, setPayments] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [paymentForm, setPaymentForm] = useState({ amount: '', type: 'Nakit', description: '' });
  const [isAddingPayment, setIsAddingPayment] = useState(false);
  const [paymentFormError, setPaymentFormError] = useState('');
  const [attachmentFile, setAttachmentFile] = useState(null);

  // Delete States
  const [deletePromptOpen, setDeletePromptOpen] = useState(false);
  const [paymentToDelete, setPaymentToDelete] = useState(null);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState('');

  // Transfer States
  const [transferMode, setTransferMode] = useState(false);
  const [newAnimalId, setNewAnimalId] = useState('');
  const [newPrice, setNewPrice] = useState('');
  const [isTransferring, setIsTransferring] = useState(false);
  const [transferError, setTransferError] = useState('');

  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [showWhatsAppMenu, setShowWhatsAppMenu] = useState(false);

  const currentAnimal = allAnimals.find(a => a.id === liveShare?.animalId);

  useEffect(() => {
    if (!isOpen || !liveShare) {
      setPayments([]);
      setTransferMode(false);
      return;
    }
    setTransferMode(initialTransferMode);
    setLoadingPayments(true);
    const q = query(collection(db, 'payments'), where('shareId', '==', liveShare.id));
    const unSub = onSnapshot(q, (snapshot) => {
       let data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
       data.sort((a, b) => {
         const aT = a.createdAt?.toMillis() || 0;
         const bT = b.createdAt?.toMillis() || 0;
         return bT - aT;
       });
       setPayments(data);
       setLoadingPayments(false);
    });
    return () => unSub();
  }, [isOpen, liveShare]);

  const handleAddPaymentSub = async (e) => {
    e.preventDefault();
    setPaymentFormError('');
    if (!paymentForm.amount || Number(paymentForm.amount) <= 0) {
      setPaymentFormError('Geçerli bir tutar girin');
      return;
    }
    if (attachmentFile && attachmentFile.size > 5 * 1024 * 1024) {
      setPaymentFormError('Ekli dosya boyutu 5 MB sınırını geçemez.');
      return;
    }
    
    setIsAddingPayment(true);
    try {
      let receiptUrl = null;
      if (attachmentFile) {
         receiptUrl = await uploadReceiptPhoto(attachmentFile);
      }

      await addPayment(liveShare.id, {
        amount: Number(paymentForm.amount),
        type: paymentForm.type,
        description: paymentForm.description,
        receiptUrl: receiptUrl
      }, currentUserUid);
      setPaymentForm({ amount: '', type: 'Nakit', description: '' });
      setAttachmentFile(null);
    } catch (err) {
      setPaymentFormError(err.message);
    } finally {
      setIsAddingPayment(false);
    }
  };

  const confirmDeletePayment = (payment) => {
    setPaymentToDelete(payment);
    setDeletePassword('');
    setDeleteError('');
    setDeletePromptOpen(true);
  };

  const executeDeletePayment = async () => {
    if (deletePassword !== "251533") {
      setDeleteError("Hatalı güvenlik şifresi!");
      return;
    }

    try {
      await deletePayment(paymentToDelete.id, liveShare.id, paymentToDelete.amount, currentUser?.email || 'Bilinmiyor');
      setDeletePromptOpen(false);
      setPaymentToDelete(null);
    } catch (err) {
      setDeleteError(err.message);
    }
  };

  // --- Transfer Handler ---
  const handleAnimalChange = (e) => {
    const aid = e.target.value;
    setNewAnimalId(aid);
    const selected = allAnimals.find(a => a.id === aid);
    if (selected && selected.price) {
      const ts = Number(selected.totalShares) || 7;
      const weight = Number(liveShare.shareWeight) || 1;
      setNewPrice(Math.round((Number(selected.price) / ts) * weight).toString());
    } else {
      setNewPrice('');
    }
  };

  const executeTransfer = async (e) => {
    e.preventDefault();
    setTransferError('');

    if (!newAnimalId || !newPrice) {
       setTransferError("Lütfen yeni bir kurban seçin ve fiyat belirleyin.");
       return;
    }

    setIsTransferring(true);
    try {
       await transferShare(liveShare.id, liveShare.animalId, newAnimalId, newPrice);
       alert("Hissedar kurbanı başarıyla değiştirildi! Tüm bakiyeler yeni kurbana aktarıldı.");
       setTransferMode(false);
    } catch (err) {
       setTransferError(err.message);
    } finally {
       setIsTransferring(false);
    }
  };

  const handleWhatsAppMessage = (type) => {
     if (!liveShare) return;
     const tagStr = currentAnimal ? currentAnimal.tagNo : "Bilinmiyor";
     const slaughterStr = currentAnimal ? currentAnimal.slaughterNo : "Bilinmiyor";
     const branchStr = currentAnimal ? currentAnimal.branch?.toUpperCase() : "Bilinmiyor";
     const fullName = `${liveShare.customerFirstName} ${liveShare.customerLastName}`;
     const remainingAmount = (liveShare.price || 0) - (liveShare.paidAmount || 0);

     let msg = '';

     if (type === 'welcome') {
       msg = `Sayın ${fullName}, Hasımoğlu Çiftliği'ne hoş geldiniz!\n\n${tagStr} küpe numaralı ve ${slaughterStr} kesim sırasındaki kurbanımıza hissedar olarak başarıyla kaydedildiniz.\nToplam hisse bedeliniz ${Number(liveShare.price || 0).toLocaleString('tr-TR')} ₺ olarak sisteme işlenmiştir.\n\nDurumunuzu anlık takip etmek için:\nhttps://hasimoglucifligi.com`;
     } else if (type === 'payment_reminder') {
       msg = `Selamın Aleyküm, kurban hisse kaydınız oluşturulmuştur. Kalan ödeme tutarınız (${remainingAmount.toLocaleString('tr-TR')} ₺) için küçük bir hatırlatma yapmak istedik. Bayram hazırlıklarımızı planlayabilmemiz için ödemenizi tamamlamanızı rica ederiz. Allah kabul etsin.`;
     } else if (type === 'proxy') {
       msg = `Sayın ${fullName}, kurban kesim işlemlerinizin İslami usullere uygun olarak gerçekleştirilebilmesi için vekaletinize ihtiyacımız vardır. Lütfen bu mesaja şu şekilde cevap vererek vekaletinizi iletiniz: "Vacip kurbanımı kesmeye, kestirmeye, bir başkasına vekalet vermeye ve dini vecibelerini yerine getirmek için sizi umumi vekil tayin ettim."`;
     } else if (type === 'deposit') {
       msg = `Sayın ${fullName}, kurban kaydınız ile alakalı olarak kapora ödemenizi gerçekleştirmeniz gereklidir. Ödemenizi yaptıktan sonra dekontunuzu bu numara üzerinden iletebilirsiniz. İyi günler dileriz.`;
     } else if (type === 'eid_greeting') {
       msg = `Sayın ${fullName}, yaklaşan Kurban Bayramınızı en içten dileklerimizle tebrik ederiz. Kestiğiniz kurbanın Hakk katında kabul olmasını dileriz. Kurbanınız ${branchStr} şubemizde, ${slaughterStr} sırasıyla kesilecektir. Selam ve dua ile... Hasımoğlu Çiftliği.`;
     } else if (type === 'survey') {
       msg = `Kurban Bayramı’nda bizleri tercih ettiğiniz için teşekkür ederiz. Sizlere daha iyi hizmet verebilmek adına hazırladığımız 2 dakikalık kısa anketimizi doldurabilirseniz çok memnun oluruz.\n\nGörüş ve önerileriniz gelecek yıllarda hizmet kalitemizi artırmamıza yardımcı olacaktır.\n\nAnket Linki: https://docs.google.com/forms/d/e/1FAIpQLSdTDehApoQm_UWTDlGyUuDy3qh9YOAB3wWBir2TGruVG2ZbXA/viewform\n\nKatkılarınız için teşekkür eder, ailenizle birlikte sağlıklı ve huzurlu günler dileriz.\n\nHaşimoğlu Besi Çiftliği`;
     }
     
     let waPhone = liveShare?.customerPhone?.replace(/\D/g, '') || '';
     if (waPhone.startsWith('0')) waPhone = waPhone.substring(1);
     if (!waPhone.startsWith('90') && waPhone.length > 0) waPhone = '90' + waPhone;
     
     if (waPhone) {
        window.open(`https://wa.me/${waPhone}?text=${encodeURIComponent(msg)}`, '_blank');
     } else {
        alert('Geçerli bir telefon numarası bulunamadı.');
     }
     setShowWhatsAppMenu(false);
  };

  if (!isOpen || !liveShare) return null;

  // Refund Hesaplaması
  const totalPaid = liveShare.paidAmount || 0;
  const simulatedDebt = Number(newPrice || 0) - totalPaid;
  const isRefundDue = simulatedDebt < 0;
  const refundAmount = Math.abs(simulatedDebt);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
       <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
       
       <div className="relative w-full max-w-3xl glass border border-navy-700 rounded-2xl overflow-hidden shadow-2xl animate-in fade-in zoom-in-95 duration-200 flex flex-col max-h-[90vh]">
          
          <div className="px-6 py-4 border-b border-navy-700/50 bg-navy-900 flex justify-between items-start">
             <div className="flex items-start gap-4">
               <div className="w-10 h-10 rounded-full bg-forest-900/50 flex items-center justify-center border border-forest-500/50 shrink-0 mt-1">
                  <Wallet className="w-5 h-5 text-forest-400" />
               </div>
               <div>
                 <h3 className="text-lg font-bold text-white tracking-tight flex items-center gap-3">
                    Tahsilat ve Hissedar Kartı
                    <div className="flex items-center gap-2">
                       <div className="relative">
                         <button 
                            onClick={() => setShowWhatsAppMenu(!showWhatsAppMenu)}
                            className="px-3 py-1 rounded-full text-[10px] font-bold tracking-wider transition-all border flex items-center gap-1 bg-green-600/20 text-green-400 border-green-500/30 hover:bg-green-500 hover:text-white"
                            title="WhatsApp Mesajı Gönder"
                         >
                            <MessageCircle className="w-3 h-3" />
                            WP BİLGİLENDİR
                         </button>

                         {showWhatsAppMenu && (
                            <>
                              <div className="fixed inset-0 z-40" onClick={() => setShowWhatsAppMenu(false)} />
                              <div className="absolute top-full left-0 mt-2 w-[18rem] bg-navy-900 border border-navy-700 shadow-2xl rounded-xl z-50 overflow-hidden flex flex-col">
                                <button onClick={() => handleWhatsAppMessage('welcome')} className="text-left px-4 py-3 text-xs font-semibold text-white hover:bg-navy-800 border-b border-navy-700/50 flex items-center gap-2 transition-colors">
                                  <MessageCircle className="w-4 h-4 text-green-400 shrink-0" /> Kayıt Onayı (Hoşgeldin)
                                </button>
                                <button onClick={() => handleWhatsAppMessage('payment_reminder')} className="text-left px-4 py-3 text-xs font-semibold text-white hover:bg-navy-800 border-b border-navy-700/50 flex items-center gap-2 transition-colors">
                                  <MessageCircle className="w-4 h-4 text-yellow-500 shrink-0" /> Ödeme Hatırlatması
                                </button>
                                <button onClick={() => handleWhatsAppMessage('proxy')} className="text-left px-4 py-3 text-xs font-semibold text-white hover:bg-navy-800 border-b border-navy-700/50 flex items-center gap-2 transition-colors">
                                  <MessageCircle className="w-4 h-4 text-blue-400 shrink-0" /> Vekalet İste
                                </button>
                                <button onClick={() => handleWhatsAppMessage('deposit')} className="text-left px-4 py-3 text-xs font-semibold text-white hover:bg-navy-800 border-b border-navy-700/50 flex items-center gap-2 transition-colors">
                                  <MessageCircle className="w-4 h-4 text-orange-400 shrink-0" /> Kapora Hatırlatması
                                </button>
                                <button onClick={() => handleWhatsAppMessage('eid_greeting')} className="text-left px-4 py-3 text-xs font-semibold text-white hover:bg-navy-800 border-b border-navy-700/50 flex items-center gap-2 transition-colors">
                                  <MessageCircle className="w-4 h-4 text-purple-400 shrink-0" /> Bayram Tebriği ve Sıra
                                </button>
                                <button onClick={() => handleWhatsAppMessage('survey')} className="text-left px-4 py-3 text-xs font-semibold text-white hover:bg-navy-800 flex items-center gap-2 transition-colors">
                                  <MessageCircle className="w-4 h-4 text-pink-400 shrink-0" /> Anket Gönder
                                </button>
                              </div>
                            </>
                         )}
                       </div>
                       {canDeleteFinance && (
                         <button 
                            onClick={() => setTransferMode(!transferMode)}
                            className={`px-3 py-1 rounded-full text-[10px] font-bold tracking-wider transition-all border flex items-center gap-1 ${transferMode ? 'bg-gold-500 text-navy-950 shadow-lg border-gold-400' : 'bg-navy-800 text-gold-400 border-gold-500/30 hover:bg-gold-500/20'}`}
                         >
                            <RefreshCw className="w-3 h-3" />
                            {transferMode ? 'TAHSİLATA DÖN' : 'KURBAN DEĞİŞTİR'}
                         </button>
                       )}
                    </div>
                 </h3>
                 <div className="flex flex-wrap items-center gap-2 mt-1.5">
                    <p className="text-sm font-semibold text-white">
                       {liveShare.customerFirstName} {liveShare.customerLastName} 
                       {liveShare.shareWeight > 1 && (
                          <span className="ml-1.5 text-[10px] bg-gold-500/20 text-gold-400 px-1.5 py-0.5 rounded-md border border-gold-500/30 align-middle tracking-wider shadow-sm">{liveShare.shareWeight} HİSSE</span>
                       )}
                       <span className="text-navy-400 ml-2 text-xs font-mono">{liveShare.customerPhone}</span>
                    </p>
                    {currentAnimal && (
                      <>
                        <span className="w-1 h-1 rounded-full bg-navy-600 mx-1"></span>
                        <span className="text-[10px] font-bold text-blue-400 bg-blue-900/30 px-2 py-0.5 rounded border border-blue-500/20">
                           ŞUBE: {currentAnimal.branch?.toUpperCase()}
                        </span>
                        <span className="text-[10px] font-bold text-forest-400 bg-forest-900/30 px-2 py-0.5 rounded border border-forest-500/20">
                           KÜPE: {currentAnimal.tagNo}
                        </span>
                        {currentAnimal.slaughterNo && (
                           <span className="text-[10px] font-bold text-gold-400 bg-gold-900/30 px-2 py-0.5 rounded border border-gold-500/20">
                              SIRA: {currentAnimal.slaughterNo}
                           </span>
                        )}
                      </>
                    )}
                 </div>
               </div>
             </div>
             <button onClick={onClose} className="text-navy-400 hover:text-white p-2 rounded-lg hover:bg-navy-800 shrink-0">✕</button>
          </div>

          <div className="p-6 overflow-y-auto custom-scrollbar">
            
            {!transferMode && (
              <div className={`grid ${liveShare.discountAmount > 0 ? 'grid-cols-2 md:grid-cols-4' : 'grid-cols-3'} gap-4 mb-6`}>
                 {liveShare.discountAmount > 0 && (
                   <div className="bg-warning-900/20 border border-warning-500/30 p-4 rounded-xl text-center shadow-inner">
                     <p className="text-[10px] text-warning-400/80 mb-1 font-bold uppercase tracking-wider">Uygulanan İndirim</p>
                     <p className="text-lg md:text-xl font-extrabold text-warning-500">{(liveShare.discountAmount || 0).toLocaleString('tr-TR')} ₺</p>
                   </div>
                 )}
                 <div className="bg-navy-900/50 border border-navy-700/50 p-4 rounded-xl text-center shadow-inner flex flex-col justify-center">
                   <p className="text-[10px] text-navy-400 mb-1 font-bold uppercase tracking-wider">{liveShare.discountAmount > 0 ? 'İndirimli Net Fiyat' : 'Toplam Ücret'}</p>
                   <p className="text-lg md:text-xl font-bold text-white">{(liveShare.price || 0).toLocaleString('tr-TR')} ₺</p>
                 </div>
                 <div className="bg-forest-900/30 border border-forest-500/30 p-4 rounded-xl text-center shadow-inner flex flex-col justify-center">
                   <p className="text-[10px] text-forest-400/80 mb-1 font-bold uppercase tracking-wider">Tahsil Edilen</p>
                   <p className="text-lg md:text-xl font-bold text-forest-400">{(liveShare.paidAmount || 0).toLocaleString('tr-TR')} ₺</p>
                 </div>
                 <div className="bg-danger-900/20 border border-danger-500/30 p-4 rounded-xl text-center shadow-inner flex flex-col justify-center">
                   <p className="text-[10px] text-danger-400/80 mb-1 font-bold uppercase tracking-wider">Kalan Borç</p>
                   <p className="text-lg md:text-xl font-bold text-danger-400">{((liveShare.price || 0) - (liveShare.paidAmount || 0)).toLocaleString('tr-TR')} ₺</p>
                 </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              
              {/* SOL KOLON: TAHSİLAT VEYA TRANSFER FORMU */}
              <div>
                 {transferMode ? (
                   <div>
                      <h4 className="text-sm font-semibold text-gold-400 mb-3 flex items-center gap-2">
                         <RefreshCw className="w-4 h-4"/> Hissedar Kurban Transfer Motoru
                      </h4>
                      <form onSubmit={executeTransfer} className="space-y-4 bg-navy-800/60 p-5 rounded-xl border border-gold-500/30">
                         {transferError && (
                           <div className="flex items-start gap-2 bg-danger-500/10 border border-danger-500/20 rounded-lg p-3">
                             <AlertCircle className="w-4 h-4 text-danger-500 shrink-0 mt-0.5" />
                             <p className="text-sm text-danger-500">{transferError}</p>
                           </div>
                         )}
                         <div>
                           <label className="block text-xs font-bold text-navy-200 mb-1">Hedef Kurbanı Seç (Aktifler)</label>
                           <select 
                              required 
                              value={newAnimalId}
                              onChange={handleAnimalChange}
                              className="w-full px-3 py-2.5 rounded-lg bg-navy-950 border border-navy-600 text-white focus:border-gold-500 outline-none text-sm"
                           >
                              <option value="">Seçiniz...</option>
                              {allAnimals.filter(a => a.status === 'active' && a.id !== liveShare.animalId && (!liveShare.branch || a.branch === liveShare.branch)).map(a => (
                                 <option key={a.id} value={a.id}>
                                    {a.branch?.toUpperCase()} | Sıra: {a.slaughterNo} | Küpe: {a.tagNo} ({a.shareCount}/{a.totalShares} Dolu)
                                 </option>
                              ))}
                           </select>
                         </div>
                         <div>
                           <label className="block text-xs font-bold text-navy-200 mb-1">Yeni Hissedar Ücreti (₺)</label>
                           <input 
                              type="number" required
                              value={newPrice} onChange={(e) => setNewPrice(e.target.value)}
                              className="w-full px-3 py-2.5 rounded-lg bg-navy-950 border border-navy-600 text-gold-400 font-extrabold text-lg focus:border-gold-500 outline-none"
                           />
                         </div>

                         {newAnimalId && newPrice && (
                            <div className="mt-4 p-4 rounded-xl border bg-navy-950 border-navy-700">
                               <p className="text-xs text-navy-400 mb-2 font-bold uppercase tracking-widest text-center border-b border-navy-800 pb-2">Önizleme Raporu</p>
                               <div className="flex justify-between text-sm mb-1 text-navy-200"><span>Ödenmiş (Kasa):</span> <span>{totalPaid} ₺</span></div>
                               <div className="flex justify-between text-sm mb-3 text-navy-200"><span>Yeni Fiyat:</span> <span>{newPrice} ₺</span></div>
                               {isRefundDue ? (
                                  <div className="bg-danger-500/20 border border-danger-500/50 rounded-lg p-3 text-center">
                                     <AlertCircle className="w-6 h-6 text-danger-400 mx-auto mb-1 animate-pulse" />
                                     <p className="text-danger-400 font-bold text-sm mb-1">KASADAN İADE GEREKİYOR</p>
                                     <p className="text-white font-extrabold text-2xl">{refundAmount.toLocaleString('tr-TR')} ₺</p>
                                     <p className="text-xs text-danger-300 mt-1">Müşterinin kasadaki nakit alacağıdır, elden teslim edilmelidir.</p>
                                  </div>
                               ) : (
                                  <div className="bg-forest-900/30 border border-forest-500/30 rounded-lg p-3 text-center">
                                     <p className="text-forest-400 font-bold text-xs mb-1">KALAN BORÇ (TAHSİL EDİLECEK)</p>
                                     <p className="text-white font-extrabold text-xl">{simulatedDebt.toLocaleString('tr-TR')} ₺</p>
                                  </div>
                               )}
                            </div>
                         )}

                         <button 
                           type="submit" disabled={isTransferring || !newAnimalId}
                           className="w-full py-3 rounded-xl font-extrabold text-navy-950 bg-gradient-to-r from-gold-500 to-yellow-400 hover:from-gold-400 disabled:opacity-50 flex justify-center items-center gap-2 shadow-lg shadow-gold-500/20 mt-2"
                         >
                           {isTransferring ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Sıfırla ve Yeni Kurbana Taşı'}
                         </button>
                      </form>
                   </div>
                 ) : (
                   <div>
                      <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                         <PlusCircle className="w-4 h-4 text-forest-400"/> Yeni Ödeme Al
                      </h4>
                      <form onSubmit={handleAddPaymentSub} className="space-y-4 bg-navy-800/40 p-5 rounded-xl border border-navy-700/50 shadow-inner">
                         {paymentFormError && (
                           <div className="flex items-start gap-2 bg-danger-500/10 border border-danger-500/20 rounded-lg p-3">
                             <AlertCircle className="w-4 h-4 text-danger-500 shrink-0 mt-0.5" />
                             <p className="text-sm text-danger-500">{paymentFormError}</p>
                           </div>
                         )}
                         <div>
                           <label className="block text-xs font-bold text-navy-300 mb-1">Tutar (₺)</label>
                           <input 
                              type="number" required
                              value={paymentForm.amount} onChange={(e) => setPaymentForm({...paymentForm, amount: e.target.value})}
                              className="w-full px-3 py-2.5 rounded-lg bg-navy-950 border border-navy-600 text-gold-400 font-bold focus:border-forest-500 outline-none"
                              placeholder="Örn: 5000"
                           />
                         </div>
                         <div>
                           <label className="block text-xs font-bold text-navy-300 mb-1">Ödeme Yöntemi</label>
                           <select 
                              value={paymentForm.type} onChange={(e) => setPaymentForm({...paymentForm, type: e.target.value})}
                              className="w-full px-3 py-2.5 rounded-lg bg-navy-950 border border-navy-600 text-white focus:border-forest-500 outline-none text-sm"
                           >
                              <option value="Nakit">Nakit</option>
                              <option value="Kredi Kartı">Kredi Kartı</option>
                              <option value="Havale/EFT">Havale/EFT</option>
                           </select>
                         </div>
                         <div>
                           <label className="block text-xs font-bold text-navy-300 mb-1">Açıklama / Not (Opsiyonel)</label>
                           <textarea 
                              value={paymentForm.description} onChange={(e) => setPaymentForm({...paymentForm, description: e.target.value})}
                              className="w-full px-3 py-2 rounded-lg bg-navy-950 border border-navy-600 text-navy-200 focus:border-forest-500 outline-none text-xs resize-none placeholder-navy-600"
                              placeholder="Örn: Hasan amca elden teslim etti..."
                              rows="2"
                           ></textarea>
                           <div className="mt-2 text-left">
                              <input 
                                type="file"
                                id="receiptAttachment"
                                className="hidden"
                                accept="image/*,application/pdf"
                                onChange={(e) => {
                                  if (e.target.files && e.target.files[0]) {
                                    setAttachmentFile(e.target.files[0]);
                                  }
                                }}
                              />
                              <label htmlFor="receiptAttachment" className="inline-flex items-center gap-1.5 cursor-pointer text-xs font-bold px-3 py-1.5 rounded-lg bg-navy-950 border border-navy-700 hover:bg-navy-900 transition-colors text-gold-400 shadow-sm whitespace-nowrap overflow-hidden text-ellipsis max-w-full">
                                 <Paperclip className="w-3 h-3 shrink-0"/> {attachmentFile ? <span className="truncate">{attachmentFile.name}</span> : 'Dekont / Görsel Seç (Max 5MB)'}
                              </label>
                              {attachmentFile && (
                                 <button type="button" onClick={() => { setAttachmentFile(null); document.getElementById('receiptAttachment').value = ''; }} className="text-danger-400 ml-2 text-xs font-bold hover:underline">Temizle</button>
                              )}
                           </div>
                         </div>
                         
                         <button 
                           type="submit" disabled={isAddingPayment || (liveShare.price - (liveShare.paidAmount || 0) <= 0)}
                           className="w-full py-3 rounded-xl font-extrabold tracking-wide text-white bg-gradient-to-r from-forest-600 to-forest-700 hover:from-forest-500 disabled:opacity-50 flex justify-center items-center gap-2 shadow-lg"
                         >
                           {isAddingPayment ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Makbuzu Sisteme İşle'}
                         </button>
                         {(liveShare.price - (liveShare.paidAmount || 0) <= 0) && (
                           <p className="text-center text-[10px] text-forest-400 mt-2 font-bold bg-forest-900/30 py-1 rounded">MÜŞTERİNİN BORCU BULUNMAMAKTADIR</p>
                         )}
                      </form>
                   </div>
                 )}
              </div>

              {/* SAĞ KOLON: İŞLEM GEÇMİŞİ */}
              <div>
                <h4 className="text-sm font-semibold text-white mb-3 flex items-center justify-between">
                   <div className="flex items-center gap-2"><FileText className="w-4 h-4 text-navy-400"/> Makbuz Geçmişi ({payments.length})</div>
                </h4>
                <div className="bg-navy-900/50 border border-navy-700/50 rounded-xl h-[310px] overflow-y-auto custom-scrollbar p-2 shadow-inner relative">
                  {loadingPayments ? (
                    <div className="absolute inset-0 flex justify-center items-center"><Loader2 className="w-6 h-6 text-forest-500 animate-spin" /></div>
                  ) : payments.length === 0 ? (
                     <div className="absolute inset-0 flex flex-col justify-center items-center opacity-50">
                        <FileText className="w-10 h-10 text-navy-400 mb-2" />
                        <p className="text-xs text-navy-300 font-bold">Kayıtlı makbuz bulunamadı.</p>
                     </div>
                   ) : (
                     <div className="space-y-2">
                       {payments.map(payment => (
                         <div key={payment.id} className="flex items-center justify-between p-3 bg-navy-800/80 rounded-xl border border-navy-600/50 hover:border-forest-500/50 transition-colors group shadow-sm">
                            <div className="flex-1 min-w-0 pr-2">
                               <div className="flex items-start gap-3">
                                  <div className={`w-10 h-10 shrink-0 rounded-full flex items-center justify-center font-bold text-xs shadow-inner mt-1
                                     ${payment.type === 'Nakit' ? 'bg-emerald-900/50 text-emerald-400 border border-emerald-500/30' : 
                                       payment.type === 'Kredi Kartı' ? 'bg-blue-900/50 text-blue-400 border border-blue-500/30' : 
                                       'bg-purple-900/50 text-purple-400 border border-purple-500/30'}`}
                                  >
                                    {payment.type.substring(0,1).toUpperCase()}{payment.type.includes('Kart') ? 'K' : payment.type.includes('EFT') ? 'H' : ''}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-0.5">
                                       <span className="text-sm font-extrabold text-white">{payment.amount.toLocaleString('tr-TR')} ₺</span>
                                       {payment.receiptNo && <span className="text-[9px] px-1.5 py-0.5 rounded border border-gold-500/30 bg-gold-500/10 text-gold-400 font-mono tracking-widest">{payment.receiptNo}</span>}
                                    </div>
                                    <p className="text-[10px] text-navy-300 flex items-center flex-wrap gap-1 font-mono">
                                      <Calendar className="w-3 h-3"/> {payment.createdAt?.toDate().toLocaleDateString('tr-TR')} 
                                      <span className="opacity-50">|</span> 
                                      <span className={`${payment.type === 'Nakit' ? 'text-emerald-400' : payment.type === 'Kredi Kartı' ? 'text-blue-400' : 'text-purple-400'}`}>{payment.type}</span>
                                    </p>
                                    {payment.description && (
                                       <p className="text-[10px] text-navy-400 mt-1 italic break-words border-l-2 border-navy-600 pl-2">"{payment.description}"</p>
                                    )}
                                    {payment.receiptUrl && (
                                       <a href={payment.receiptUrl} target="_blank" rel="noreferrer" className="inline-flex mt-1 items-center gap-1 text-[10px] font-bold text-gold-400 bg-gold-900/10 px-2 py-0.5 rounded border border-gold-500/20 hover:bg-gold-500/20 transition-colors cursor-pointer" title="Ekli dekontu görmek için tıkla">
                                          <ImageIcon className="w-3 h-3"/> Dekontu Görüntüle
                                       </a>
                                    )}
                                  </div>
                               </div>
                            </div>
                            <div className="flex shrink-0 gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                               <button 
                                  type="button"
                                  onClick={() => setSelectedReceipt(payment)}
                                  className="text-gold-400/80 hover:text-gold-300 p-2 rounded-lg hover:bg-gold-500/20 transition-all disabled:opacity-50"
                                  title="Makbuzu Görüntüle ve Yazdır"
                               >
                                  <Printer className="w-4 h-4" />
                               </button>
                            {canDeleteFinance && (
                               <button 
                                  type="button"
                                  onClick={() => confirmDeletePayment(payment)}
                                  className="text-danger-500/50 hover:text-danger-400 p-2 border-l border-navy-700/50 rounded-r-lg hover:bg-danger-500/20 transition-all"
                                  title="Makbuzu İptal Et (Sadece S.Admin)"
                               >
                                  <Trash2 className="w-4 h-4" />
                               </button>
                            )}
                            </div>
                         </div>
                       ))}
                     </div>
                   )}
                </div>
                
                {payments.length > 0 && (() => {
                  const tagStr = currentAnimal ? currentAnimal.tagNo : "Bilinmiyor";
                  const slaughterStr = currentAnimal ? currentAnimal.slaughterNo : "Bilinmiyor";
                  const msg = `*HAŞİMOĞLU ÇİFTLİĞİ*\n\n` +
                              `Sayın ${liveShare.customerFirstName} ${liveShare.customerLastName}, kurbanlık hisseniz için tahsilat işlemi gerçekleşmiştir.\n\n` +
                              `Küpe No: ${tagStr}\n` +
                              `Kesim Sırası: ${slaughterStr}\n\n` +
                              `Toplam Hisse Tutarı: ${liveShare.price} ₺\n` +
                              `Yapılan Ödemeler: ${liveShare.paidAmount} ₺\n` +
                              `Kalan Borcunuz: ${liveShare.price - liveShare.paidAmount} ₺\n\n` +
                              `Kurban ibadetinizin kabul olmasını diler, bizi tercih ettiğiniz için teşekkür ederiz.\n\n` + 
                              `Durumunuzu anlık takip etmek için:\nhttps://hasimoglucifligi.com`;
                  return (
                  <div className="mt-3 text-center">
                    <a 
                      href={`https://wa.me/${formatWhatsAppNumber(liveShare.customerPhone)}?text=${encodeURIComponent(msg)}`} 
                      target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1 text-[11px] text-forest-400 hover:text-white bg-forest-900/20 hover:bg-forest-600 transition-colors px-3 py-1.5 rounded-full font-bold border border-forest-500/20"
                    >
                      <ChevronRight className="w-3 h-3" /> Whatsapp Müşteri Tahsilat Makbuzu (Otomatik)
                    </a>
                  </div>
                  )
                })()}
              </div>
            </div>
          </div>
       </div>

       {deletePromptOpen && paymentToDelete && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
           <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={() => setDeletePromptOpen(false)} />
           <div className="relative w-full max-w-sm glass border border-danger-500/50 rounded-2xl overflow-hidden shadow-2xl p-6 bg-navy-900 animate-in zoom-in-95">
              <div className="flex flex-col items-center text-center">
                 <div className="w-12 h-12 rounded-full bg-danger-500/20 flex items-center justify-center mb-4 border border-danger-500/50">
                    <AlertCircle className="w-6 h-6 text-danger-500" />
                 </div>
                 <h3 className="text-lg font-bold text-white mb-2">Güvenlik Doğrulaması</h3>
                 <p className="text-xs text-navy-300 mb-6">
                    <span className="font-bold text-white">{paymentToDelete.amount.toLocaleString('tr-TR')} ₺</span> tutarındaki makbuzu silmek üzeresiniz. Lütfen işlemi onaylamak için Süper Admin şifresini girin.
                 </p>
                 
                 {deleteError && (
                    <div className="w-full bg-danger-500/10 border border-danger-500/20 text-danger-500 text-xs py-2 rounded mb-4">
                       {deleteError}
                    </div>
                 )}

                 <input 
                    type="password" 
                    placeholder="Şifreyi giriniz..."
                    value={deletePassword}
                    onChange={(e) => setDeletePassword(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg bg-navy-950 border border-navy-700 text-white focus:border-danger-500 outline-none mb-6 text-center text-lg tracking-widest"
                    autoFocus
                 />

                 <div className="flex gap-3 w-full">
                    <button 
                       onClick={() => setDeletePromptOpen(false)}
                       className="flex-1 py-2.5 rounded-lg bg-navy-800 text-navy-300 hover:text-white transition-colors text-sm font-semibold"
                    >
                       İptal
                    </button>
                    <button 
                       onClick={executeDeletePayment}
                       className="flex-1 py-2.5 rounded-lg bg-danger-600 hover:bg-danger-500 text-white transition-colors text-sm font-bold shadow-lg shadow-danger-500/20"
                    >
                       Kalıcı Olarak Sil
                    </button>
                 </div>
              </div>
           </div>
        </div>
      )}
      
      {selectedReceipt && (
         <ReceiptTemplate 
            payment={selectedReceipt} 
            liveShare={liveShare} 
            currentAnimal={currentAnimal}
            onClose={() => setSelectedReceipt(null)}
         />
      )}
    </div>
  );
}
